import { useEffect, useRef, useState } from "react";
import { Form, Input, DatePicker, Select, Skeleton } from "../../components/ui";
import {
  useAddTeacher,
  useGetTeacherById,
  useSearchClassroom,
  useUpdateTeacher,
} from "../../query/teacherQuery";
import swal from "../../utils/swal";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, X } from "lucide-react";
import { UPLOADS_URL } from "../../config/constants";
import { applyAddPassword, makeTeacherPassword } from "../../config/helpers";
import FormPhotoDrop from "../../components/FormPhotoDrop";
import "./addTeacher.css";

function asDateString(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (value.format) return value.format("YYYY-MM-DD");
  return "";
}

function toMonth(value) {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM") : "";
}

const TODAY = dayjs().format("YYYY-MM-DD");

function phoneRule(_, value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) throw new Error("Please enter a phone number");
  if (digits.length < 10 || digits.length > 13) {
    throw new Error("Enter a phone number like 0300 1234567");
  }
}

function AddTeacher() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form] = Form.useForm();
  const { mutate: addTeacher, isPending: isAdding } = useAddTeacher();
  const { mutate: updateTeacher, isPending: isUpdating } = useUpdateTeacher();
  const { data: classrooms } = useSearchClassroom({});
  const { data: teacher, isLoading } = useGetTeacherById(isEdit ? id : undefined);
  const intentRef = useRef("ACTIVE");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const isMutating = isAdding || isUpdating;

  useEffect(() => {
    if (!isEdit || !teacher) return;

    const employment = teacher.employment?.[0] || {};
    const educationRows = (teacher.education || []).map((item) => ({
      school: item.school || "",
      degree: (item.subject || []).filter(Boolean).join(", "),
      start: toMonth(item.start),
      end: toMonth(item.end),
    }));
    form.setFieldsValue({
      firstName: teacher.firstName || "",
      lastName: teacher.lastName || "",
      email: teacher.email || "",
      phone: teacher.phone || "",
      address: teacher.address || "",
      dateOfBirth: teacher.dateOfBirth ? dayjs(teacher.dateOfBirth) : "",
      placeOfBirth: teacher.placeOfBirth || "",
      city: teacher.city || "",
      state: teacher.state || "",
      zip: teacher.zip || "",
      homeNumber: teacher.homeNumber || "",
      teacherId: teacher.teacherId || "",
      classroom: teacher.classroom?._id || teacher.classroom || "",
      education: educationRows.length ? educationRows : [{}],
      empSchool: employment.school || "",
      empPosition: employment.position || "",
      empAddress: employment.address || "",
      empStart: toMonth(employment.start),
      empEnd: toMonth(employment.end),
      bio: teacher.bio || "",
    });

    if (teacher.image) {
      setImagePreview(`${UPLOADS_URL}/${teacher.image}`);
    }
    intentRef.current = teacher.status === "PENDING" ? "PENDING" : "ACTIVE";
  }, [isEdit, teacher, form]);

  const onFinish = (values) => {
    if (!isEdit && !imageFile) {
      swal("Oops!", "Please upload a photo", "error");
      return;
    }

    const formObject = new FormData();
    if (imageFile) formObject.append("image", imageFile);
    formObject.append("firstName", values.firstName || "");
    formObject.append("lastName", values.lastName || "");
    formObject.append("email", values.email || "");
    formObject.append("phone", values.phone || "");
    formObject.append("address", values.address || "");
    formObject.append("dateOfBirth", asDateString(values.dateOfBirth));
    formObject.append("placeOfBirth", values.placeOfBirth || "");
    formObject.append("city", values.city || "");
    formObject.append("state", values.state || "");
    formObject.append("zip", values.zip || "");
    formObject.append("homeNumber", values.homeNumber || "");
    formObject.append("bio", values.bio || "");
    formObject.append("status", intentRef.current === "PENDING" ? "PENDING" : "ACTIVE");

    if (values.teacherId) formObject.append("teacherId", values.teacherId);
    if (values.classroom) formObject.append("classroom", values.classroom);
    if (!isEdit && values.password) formObject.append("password", values.password);

    formObject.append(
      "education",
      JSON.stringify(
        (values.education || [])
          .filter((item) => item?.school || item?.degree)
          .map((item) => ({
            school: item.school || "",
            subject: (item.degree || "")
              .split(",")
              .map((part) => part.trim())
              .filter(Boolean),
            start: item.start || "",
            end: item.end || "",
          }))
      )
    );

    formObject.append(
      "employment",
      JSON.stringify(
        values.empSchool || values.empPosition
          ? [
              {
                school: values.empSchool || "",
                address: values.empAddress || "",
                position: values.empPosition || "",
                start: values.empStart || "",
                end: values.empEnd || "",
              },
            ]
          : []
      )
    );

    if (isEdit) {
      updateTeacher(
        { teacherId: id, data: formObject },
        {
          onSuccess: () => {
            swal("Success", "Teacher updated successfully", "success");
            navigate(`/teachers/${id}`);
          },
          onError: (error) => {
            swal("Error", error?.message || "Failed to update teacher", "error");
          },
        }
      );
      return;
    }

    addTeacher(
      { data: formObject },
      {
        onSuccess: (result) => {
          const payload = result?.data || {};
          const drafted = intentRef.current === "PENDING";
          const lines = [
            drafted ? "Teacher saved as draft" : "Teacher added successfully",
          ];
          if (payload.teacherId || payload.teacher?.teacherId) {
            lines.push(`Teacher ID: ${payload.teacherId || payload.teacher.teacherId}`);
          }
          if (payload.password || values.password) {
            lines.push(`Password: ${values.password || payload.password}`);
          }
          swal("Success", lines.join("\n"), "success");
          navigate("/teachers");
        },
        onError: (error) => {
          swal("Error", error?.message || "Failed to add teacher", "error");
        },
      }
    );
  };

  if (isEdit && isLoading) {
    return (
      <div className="tf-page">
        <Skeleton active />
      </div>
    );
  }

  return (
    <div className="tf-page">
      <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off" initialValues={{ education: [{}] }}>
        <section className="tf-card">
          <h2 className="tf-card-head">Personal Details</h2>
          <div className="tf-card-body">
            <FormPhotoDrop
              preview={imagePreview}
              required={!isEdit}
              alt="Teacher preview"
              onFile={(file) => {
                setImageFile(file);
                setImagePreview((prev) => {
                  if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
                  return URL.createObjectURL(file);
                });
              }}
            />

            <div className="tf-field">
              <p className="tf-label">
                First name <span>*</span>
              </p>
              <Form.Item
                name="firstName"
                className="tf-item mb-0"
                rules={[
                  { required: true, message: "Please enter first name" },
                  { min: 3, message: "First name must be at least 3 characters" },
                ]}
              >
                <Input autoComplete="given-name" placeholder="Ayesha" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Last name <span>*</span>
              </p>
              <Form.Item
                name="lastName"
                className="tf-item mb-0"
                rules={[
                  { required: true, message: "Please enter last name" },
                  { min: 3, message: "Last name must be at least 3 characters" },
                ]}
              >
                <Input autoComplete="family-name" placeholder="Khan" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Email <span>*</span>
              </p>
              <Form.Item
                name="email"
                className="tf-item mb-0"
                rules={[
                  { required: true, message: "Please enter email" },
                  { type: "email", message: "Please enter a valid email" },
                ]}
              >
                <Input type="email" autoComplete="email" inputMode="email" placeholder="ayesha.khan@school.com" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Mobile number <span>*</span>
              </p>
              <Form.Item
                name="phone"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please enter a phone number" }, { validator: phoneRule }]}
              >
                <Input type="tel" autoComplete="tel" inputMode="tel" placeholder="0300 1234567" />
              </Form.Item>
            </div>

            <div className="tf-field is-full">
              <p className="tf-label">
                Street address <span>*</span>
              </p>
              <Form.Item
                name="address"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please enter address" }]}
              >
                <Input autoComplete="street-address" placeholder="House 12, Block B, Gulshan-e-Iqbal" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Date of birth {!isEdit ? <span>*</span> : null}
              </p>
              <Form.Item
                name="dateOfBirth"
                className="tf-item mb-0"
                rules={isEdit ? [] : [{ required: true, message: "Please choose a date of birth" }]}
              >
                <DatePicker max={TODAY} placeholder="Choose a date" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Place of birth {!isEdit ? <span>*</span> : null}
              </p>
              <Form.Item
                name="placeOfBirth"
                className="tf-item mb-0"
                rules={isEdit ? [] : [{ required: true, message: "Please enter place of birth" }]}
              >
                <Input placeholder="Karachi" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                City <span>*</span>
              </p>
              <Form.Item
                name="city"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please enter city" }]}
              >
                <Input autoComplete="address-level2" placeholder="Karachi" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">Province</p>
              <Form.Item name="state" className="tf-item mb-0">
                <Input autoComplete="address-level1" placeholder="Sindh" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">Postal code</p>
              <Form.Item name="zip" className="tf-item mb-0">
                <Input inputMode="numeric" autoComplete="postal-code" maxLength={10} placeholder="75300" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">Home phone</p>
              <Form.Item name="homeNumber" className="tf-item mb-0">
                <Input type="tel" inputMode="tel" placeholder="021 12345678" />
              </Form.Item>
            </div>
          </div>
        </section>

        <section className="tf-card">
          <h2 className="tf-card-head">Account & Class</h2>
          <div className="tf-card-body">
            <div className="tf-field">
              <p className="tf-label">Teacher ID</p>
              <Form.Item name="teacherId" className="tf-item mb-0">
                <Input placeholder={isEdit ? "Teacher ID" : "Leave blank to generate one"} />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Classroom <span>*</span>
              </p>
              <Form.Item
                name="classroom"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please select a classroom" }]}
              >
                <Select
                  placeholder="Select a classroom"
                  options={(classrooms || []).map((room) => ({
                    value: room._id,
                    label: room.classroomId
                      ? `${room.classroomName} (${room.classroomId})`
                      : room.classroomName,
                  }))}
                />
              </Form.Item>
            </div>

            {!isEdit ? (
              <div className="tf-field is-full">
                <p className="tf-label">Password</p>
                <div className="tf-password-row">
                  <Form.Item name="password" className="tf-item mb-0">
                    <Input.Password autoComplete="new-password" placeholder="Leave blank to generate one" />
                  </Form.Item>
                  <button
                    type="button"
                    className="tf-gen"
                    onClick={() => applyAddPassword(form, makeTeacherPassword())}
                  >
                    Generate
                  </button>
                </div>
                <p className="tf-hint">If you leave this empty, a temporary password is created and shown after submit.</p>
              </div>
            ) : null}
          </div>
        </section>

        <section className="tf-card">
          <h2 className="tf-card-head">Education</h2>
          <Form.List name="education" initialValue={[{}]}>
            {(fields, { add, remove }) => (
              <div className="tf-repeat-wrap">
                {fields.map((field, index) => (
                  <div key={field.key} className="tf-repeat">
                    <div className="tf-repeat-head">
                      <p>Education {index + 1}</p>
                      {fields.length > 1 ? (
                        <button
                          type="button"
                          className="tf-repeat-remove"
                          onClick={() => remove(field.name)}
                          aria-label="Remove education"
                        >
                          <X size={16} />
                          Remove
                        </button>
                      ) : null}
                    </div>
                    <div className="tf-card-body">
                      <div className="tf-field">
                        <p className="tf-label">
                          University <span>*</span>
                        </p>
                        <Form.Item
                          name={[field.name, "school"]}
                          className="tf-item mb-0"
                          rules={[{ required: true, message: "Please enter university" }]}
                        >
                          <Input placeholder="University of Karachi" />
                        </Form.Item>
                      </div>

                      <div className="tf-field">
                        <p className="tf-label">
                          Degree <span>*</span>
                        </p>
                        <Form.Item
                          name={[field.name, "degree"]}
                          className="tf-item mb-0"
                          rules={[{ required: true, message: "Please enter degree" }]}
                        >
                          <Input placeholder="B.Ed, Education" />
                        </Form.Item>
                      </div>

                      <div className="tf-field">
                        <p className="tf-label">
                          Started <span>*</span>
                        </p>
                        <Form.Item
                          name={[field.name, "start"]}
                          className="tf-item mb-0"
                          rules={[{ required: true, message: "Please choose a start month" }]}
                        >
                          <Input type="month" />
                        </Form.Item>
                      </div>

                      <div className="tf-field">
                        <p className="tf-label">
                          Finished <span>*</span>
                        </p>
                        <Form.Item
                          name={[field.name, "end"]}
                          className="tf-item mb-0"
                          rules={[{ required: true, message: "Please choose an end month" }]}
                        >
                          <Input type="month" />
                        </Form.Item>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="tf-repeat-add">
                  <button type="button" className="tf-add-row" onClick={() => add({})}>
                    <Plus size={16} />
                    Add Education
                  </button>
                </div>
              </div>
            )}
          </Form.List>
        </section>

        <section className="tf-card">
          <h2 className="tf-card-head">Work Experience</h2>
          <div className="tf-card-body">
            <div className="tf-field">
              <p className="tf-label">School or workplace</p>
              <Form.Item name="empSchool" className="tf-item mb-0">
                <Input placeholder="City School" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">Role</p>
              <Form.Item name="empPosition" className="tf-item mb-0">
                <Input placeholder="Class teacher" />
              </Form.Item>
            </div>

            <div className="tf-field is-full">
              <p className="tf-label">Workplace address</p>
              <Form.Item name="empAddress" className="tf-item mb-0">
                <Input placeholder="Clifton, Karachi" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">Started</p>
              <Form.Item name="empStart" className="tf-item mb-0">
                <Input type="month" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">Finished</p>
              <Form.Item name="empEnd" className="tf-item mb-0">
                <Input type="month" />
              </Form.Item>
            </div>

            <div className="tf-field is-full">
              <p className="tf-label">Short bio</p>
              <Form.Item name="bio" className="tf-item mb-0">
                <Input.TextArea className="is-short" rows={4} placeholder="A short note about this teacher" />
              </Form.Item>
            </div>
          </div>
        </section>

        <div className="tf-actions">
          <button
            type="submit"
            className="tf-btn is-ghost"
            disabled={isMutating}
            onClick={() => {
              intentRef.current = "PENDING";
            }}
          >
            {isMutating && intentRef.current === "PENDING" ? "Saving..." : "Save as Draft"}
          </button>
          <button
            type="submit"
            className="tf-btn is-primary"
            disabled={isMutating}
            onClick={() => {
              intentRef.current = "ACTIVE";
            }}
          >
            {isMutating && intentRef.current === "ACTIVE"
              ? "Saving..."
              : isEdit
              ? "Save"
              : "Submit"}
          </button>
        </div>
      </Form>
    </div>
  );
}

export default AddTeacher;
